/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';
import { observable, computed, action, toJS, makeObservable } from 'mobx';
import _isNil from 'lodash/isNil';
import _find from 'lodash/find';

import type { Product } from 'utils/helpers/product';
import type CartStore from './cart';
import type { CartAddItemInput, BreakdownItem, ReceiptProduct, CartItemJSON } from './cart.types';

export default class CartItem {
  @observable product: Product;
  @observable quantity = 0;
  @observable option = '';
  @observable rawOption: string | null | undefined = '';
  @observable id: string | null = null;
  @observable trackerSource: string | null = null;

  additionalOption: string | undefined;
  key: string;
  cart: CartStore;

  constructor(cart: CartStore, item: CartAddItemInput, key: string) {
    makeObservable(this);

    this.cart = cart;
    this.product = item.product;
    this.quantity = Number(item.quantity);
    this.option = item.option;
    this.rawOption = item.product?.rawOptions?.[this.optionIndex];
    this.additionalOption = item.additionalOption;
    if (!item.additionalOption && item.product?.AdditionalOptions?.[0]) {
      [this.additionalOption] = item.product.AdditionalOptions;
    }
    this.id = item.product?.id ?? null;
    this.key = key;
    this.trackerSource = item.trackerSource ?? null;
  }

  @action
  add(item: CartAddItemInput): void {
    if (this.product.id !== item.product.id || this.option !== item.option) {
      return;
    }
    this.quantity += item.quantity;
  }

  @computed
  get optionIndex(): number {
    return this.product.Options!.indexOf(this.option);
  }

  @computed
  get basePrice(): number {
    return parseFloat(
      String((this.cart.isMedical ? this.product.medicalPrices! : this.product.recPrices!)[this.optionIndex]!)
    );
  }

  getBreakdownItem(): BreakdownItem | null | undefined {
    return _find(
      this.cart?.costBreakdown?.details || [],
      (detail) => this.id === detail?.id && (this.option || `N/A`) === (detail.option || `N/A`)
    );
  }

  getReceiptItem(): ReceiptProduct | undefined {
    return _find(
      this.cart?.costBreakdown?.receipt?.details?.products || [],
      (product) => `${product.id}-${product.option}` === this.key
    );
  }

  @computed
  get bogoDiscount(): string | null {
    const breakdownItem = this.getBreakdownItem();
    const bogoDiscount = breakdownItem?.bogoSavings?.menuBogoDiscount;
    return !_isNil(bogoDiscount) ? bogoDiscount : null;
  }

  /** @returns {boolean | null} */
  @computed
  get discountToCartApplied(): boolean | null {
    const breakdownItem = this.getBreakdownItem();
    const isDiscountToCart = breakdownItem?.bogoSavings?.isDiscountToCartReward;
    return !_isNil(isDiscountToCart) ? isDiscountToCart : null;
  }

  @computed
  get discountedBasePrice(): number | null {
    if (hasSaleSpecialForMenuType({ product: this.product, medicalOrder: this.cart.isMedical })) {
      return parseFloat(
        String(
          (this.cart.isMedical ? this.product.medicalSpecialPrices! : this.product.recSpecialPrices!)[this.optionIndex]!
        )
      );
    }
    return null;
  }

  @computed
  get individualPrice(): number {
    return !_isNil(this.discountedBasePrice) ? this.discountedBasePrice : this.basePrice;
  }

  @computed
  get price(): number {
    return this.unitPrice * this.quantity;
  }

  @computed
  get saleAdjustment(): number | null {
    const breakdownItem = this.getBreakdownItem();
    return breakdownItem?.menuSaleAdjustment ?? null;
  }

  @computed
  get unitPrice(): number {
    return this.discountedBasePrice ?? this.basePrice;
  }

  toJSON(): CartItemJSON {
    const product = toJS(this.product);
    product._id = product.id;
    return {
      product,
      quantity: this.quantity,
      option: this.option,
      rawOption: this.rawOption,
      additionalOption: this.additionalOption,
      basePrice: this.basePrice,
      discountedBasePrice: this.discountedBasePrice,
      unitPrice: this.unitPrice,
      price: this.price,
      key: this.key,
      trackerSource: this.trackerSource,
    };
  }
}
